import React, { useMemo, useState } from 'react';
import { Button, Tag, Typography } from 'antd';

import { formatBytes } from '../../utils/formatters';
import { fileFactsFor, showsAsPicture } from '../../utils/fileValues';
import { normalizeId } from '../../utils/ids';
import { publicDownloadNote } from '../../utils/publicFiles';
import { getFileTypeIcon } from './AttachmentsSection';
import { PublicFileDownload, PublicPhotoViewer, opensFullScreen } from './PublicFiles';

const { Text } = Typography;

/**
 * An item link's Other attachments on the public page (PR 10c; G13 — "Include
 * files" covers file elements AND Other attachments): the signed-in
 * section's rows, read-only — the inline `sm` for a photo (else the type
 * icon), the name, the type and size, and Download or "Sign in to download"
 * — or, under the malware gate (PR 10e), the line that says why not ("Checking
 * this file…", "This file isn't available."), under the facts where a narrow
 * row has room for it. A photo opens its `lg` full screen. Never who uploaded
 * it, never when.
 *
 * `attachments` is the payload's list, each `{ id, name, ext, size, image,
 * variants, download, thumb }`; an entry without an id, or with no download
 * rule (not a public entry), is dropped. Nothing at all renders nothing.
 */
export default function PublicAttachments({ code, attachments }) {
	const files = useMemo(() => (Array.isArray(attachments) ? attachments : [])
		.map((entry) => {
			const id = normalizeId(entry?.id ?? entry?._id);
			return id ? fileFactsFor({ files: { [id]: entry } }, id) : null;
		})
		.filter(file => file && file.isPublic), [attachments]);
	const [viewing, setViewing] = useState(null);

	if (!files.length) return null;

	return (
		<section className="attachments attachments--public" aria-label="Attachments">
			<div className="attachments__header">
				<div className="micro-label attachments__title">Attachments</div>
			</div>
			<ul className="attachments__list public-attachments__list">
				{files.map((file) => {
					const TypeIcon = getFileTypeIcon(file.ext);
					const name = file.originalName || 'File';
					const opens = opensFullScreen(file);
					const thumb = showsAsPicture(file, 'sm') ? file.thumb : null;
					const note = publicDownloadNote(file);
					return (
						<li key={file._id} className="attachments__row">
							<span className="attachments__thumb" aria-hidden="true">
								{thumb ? <img className="attachments__thumb-img" src={thumb} alt="" draggable={false} /> : <TypeIcon />}
							</span>
							<div className="attachments__main">
								{opens ? (
									<Button type="link" className="attachments__name" title={name} aria-label={`Open ${name}`} onClick={() => setViewing(file)}>
										{name}
									</Button>
								) : <span className="attachments__name" title={name}>{name}</span>}
								<div className="attachments__meta">
									{file.ext ? <Tag className="attachments__ext">{file.ext.toUpperCase()}</Tag> : null}
									{Number.isFinite(file.size) ? <Text type="secondary" className="attachments__facts">{formatBytes(file.size)}</Text> : null}
								</div>
								{note ? <PublicFileDownload code={code} file={file} /> : null}
							</div>
							{note ? null : (
								<span className="attachments__row-actions">
									<PublicFileDownload code={code} file={file} compact />
								</span>
							)}
						</li>
					);
				})}
			</ul>
			<PublicPhotoViewer code={code} file={viewing} onClose={() => setViewing(null)} />
		</section>
	);
}
